import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import { listLimits, resetLimit, setLimit, type Limit } from '../../api/admin';
import { Alert, Badge, Button, Card, LoadingState } from '../ui';

/**
 * The limits, as this instance runs them.
 *
 * Each one says what it protects and the range it cannot be taken outside —
 * a number with no stated consequence is a number nobody can set responsibly.
 * "Configured" means what the environment set; "Changed here" means somebody
 * moved it, and the button puts it back.
 */
export function AdminLimits() {
  const [limits, setLimits] = useState<Limit[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<ApiError | null>(null);
  const [working, setWorking] = useState('');

  const load = useCallback(async () => {
    try {
      const answer = await listLimits();
      setLimits(answer.limits);
      setDrafts(Object.fromEntries(answer.limits.map((limit) => [limit.key, String(limit.value)])));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
      setLimits([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (key: string, action: () => Promise<unknown>) => {
    setWorking(key);
    setError(null);
    try {
      await action();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking('');
    }
  };

  if (limits === null) {
    return <LoadingState label="Reading the limits…" />;
  }

  return (
    <>
      {error && (
        <Alert tone="warning" title="That limit was not changed">
          {error.message}
        </Alert>
      )}

      <ul className="sc-admin__limits">
        {limits.map((limit) => (
          <li key={limit.key}>
            <Card>
              <div className="sc-admin__limit-head">
                <h3>{limit.label}</h3>
                <Badge tone={limit.is_overridden ? 'accent' : 'neutral'}>
                  {limit.is_overridden ? 'Changed here' : 'Configured'}
                </Badge>
              </div>
              <p className="sc-admin__muted">{limit.description}</p>
              <p className="sc-admin__guards">Protects: {limit.guards}</p>

              <div className="sc-admin__limit-set">
                {limit.kind === 'bool' ? (
                  <label className="sc-admin__inline">
                    <input
                      type="checkbox"
                      checked={drafts[limit.key] === 'true'}
                      disabled={working === limit.key}
                      onChange={(event) =>
                        void run(limit.key, () =>
                          setLimit(limit.key, event.target.checked ? 'true' : 'false'),
                        )
                      }
                    />
                    <span>{drafts[limit.key] === 'true' ? 'On' : 'Off'}</span>
                  </label>
                ) : (
                  <>
                    <input
                      aria-label={limit.label}
                      className="sc-admin__number"
                      type="number"
                      min={limit.minimum}
                      max={limit.maximum}
                      value={drafts[limit.key] ?? ''}
                      onChange={(event) =>
                        setDrafts({ ...drafts, [limit.key]: event.target.value })
                      }
                    />
                    <span className="sc-admin__muted">
                      {limit.minimum}–{limit.maximum}
                    </span>
                    <Button
                      size="sm"
                      isLoading={working === limit.key}
                      // Saving writes an override, so an untouched value must
                      // not be savable: it would mark a limit as changed by
                      // hand when nobody changed it.
                      disabled={(drafts[limit.key] ?? '') === String(limit.value)}
                      onClick={() =>
                        void run(limit.key, () => setLimit(limit.key, drafts[limit.key] ?? ''))
                      }
                    >
                      Save
                    </Button>
                  </>
                )}
                {limit.is_overridden && (
                  <Button
                    size="sm"
                    variant="secondary"
                    isLoading={working === limit.key}
                    onClick={() => void run(limit.key, () => resetLimit(limit.key))}
                  >
                    Put it back
                  </Button>
                )}
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </>
  );
}
